import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {number | null} */
  #desktopStateTimeout = null;
  /** @type {boolean} */
  #desktopPickerHydrated = false;

  /**
   * Returns the nearest product card element.
   * @returns {Element | null}
   */
  #getProductCardElement() {
    return this.closest('product-card');
  }

  /**
   * Returns the product card link for upgraded and non-upgraded product cards.
   * @returns {HTMLAnchorElement | null}
   */
  #getProductCardLink() {
    const productCard = this.#getProductCardElement();
    if (!productCard) return null;

    if (
      'getProductCardLink' in productCard &&
      typeof productCard.getProductCardLink === 'function'
    ) {
      return productCard.getProductCardLink();
    }

    const fallbackLink = productCard.querySelector('[ref="productCardLink"]');
    return fallbackLink instanceof HTMLAnchorElement ? fallbackLink : null;
  }

  get productPageUrl() {
    const productLink = this.#getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = this.#getProductCardElement();
    if (!productCard) return null;

    if (
      'getSelectedVariantId' in productCard &&
      typeof productCard.getSelectedVariantId === 'function'
    ) {
      return productCard.getSelectedVariantId();
    }

    const checkedInput = /** @type {HTMLInputElement | null} */ (
      productCard.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Strips leading brand/category prefixes to match product title block formatting.
   * @param {string} title
   * @returns {string}
   */
  #sanitizePrimaryTitle(title) {
    return title.replace(/^(?:(?:AFENDS|Afends|Womens|Mens|Unisex)\s*)+/u, '').trim();
  }

  /**
   * Builds mobile quick add title markup to match product title behavior.
   * @param {string} fullTitle
   * @returns {HTMLAnchorElement}
   */
  #buildMobileProductTitleLink(fullTitle) {
    const productTitle = document.createElement('a');
    const [rawPrimary = '', rawSecondary = ''] = fullTitle.split(' - ');
    const primary = this.#sanitizePrimaryTitle(rawPrimary);
    const secondary = rawSecondary;
    const secondaryOrPrimary = secondary || primary;

    const primaryLine = document.createElement('p');
    primaryLine.className = 'af-plp--title-primary';
    primaryLine.append(document.createTextNode(primary));

    if (secondary) {
      const hiddenSecondary = document.createElement('span');
      hiddenSecondary.className = 'visually-hidden';
      hiddenSecondary.textContent = ` - ${secondary}`;
      primaryLine.append(document.createTextNode(' '), hiddenSecondary);
    }

    const secondaryLine = document.createElement('p');
    secondaryLine.className = 'af-plp--title-secondary';
    secondaryLine.textContent = secondaryOrPrimary;

    productTitle.append(primaryLine, secondaryLine);
    productTitle.href = this.productPageUrl;

    return productTitle;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.addEventListener('click', this.#handleDesktopVariantClick);
    this.addEventListener('change', this.#handleDesktopVariantChange);
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleDesktopCartUpdate);

    this.#scheduleDesktopPickerHydration();
    this.#scheduleModalPrefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('click', this.#handleDesktopVariantClick);
    this.removeEventListener('change', this.#handleDesktopVariantChange);
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleDesktopCartUpdate);
    this.#abortController?.abort();
    if (this.#desktopStateTimeout) {
      clearTimeout(this.#desktopStateTimeout);
      this.#desktopStateTimeout = null;
    }
  }

  /**
   * Adds to cart when a desktop variant radio is clicked, including re-clicking the
   * already-selected option (which does not fire a change event).
   * @param {MouseEvent} event
   */
  #handleDesktopVariantClick = (event) => {
    if (isMobileBreakpoint()) return;

    this.#hydrateDesktopVariantPicker();

    const picker =
      event.target instanceof Element ? event.target.closest('.quick-add__desktop-variant-picker') : null;
    if (!picker) return;

    const label = event.target instanceof Element ? event.target.closest('.variant-option__button-label') : null;
    if (!label || !picker.contains(label)) return;

    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    this.#submitDesktopQuickAdd();
  };

  /**
   * Adds selected desktop variant from dropdown changes in the product card.
   * @param {Event} event
   */
  #handleDesktopVariantChange = (event) => {
    if (isMobileBreakpoint()) return;

    this.#hydrateDesktopVariantPicker();

    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.closest('.quick-add__desktop-variant-picker')) return;

    this.#submitDesktopQuickAdd();
  };

  /** Submits the quick-add form for desktop inline variant selection. */
  #submitDesktopQuickAdd() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    if (this.#desktopStateTimeout) {
      clearTimeout(this.#desktopStateTimeout);
      this.#desktopStateTimeout = null;
    }

    this.toggleAttribute('stay-visible', true);
    this.dataset.desktopQuickAddState = 'loading';
    form.requestSubmit();
  }

  /** @param {CustomEvent} event */
  #handleDesktopCartUpdate = (event) => {
    if (isMobileBreakpoint()) return;
    if (this.dataset.desktopQuickAddState !== 'loading') return;

    if (event.detail?.data?.didError) {
      this.#resetDesktopQuickAddState();
      return;
    }

    this.dataset.desktopQuickAddState = 'added';
    this.#desktopStateTimeout = window.setTimeout(() => {
      this.#resetDesktopQuickAddState();
    }, 2800);
  };

  #resetDesktopQuickAddState() {
    delete this.dataset.desktopQuickAddState;
    this.toggleAttribute('stay-visible', false);
    if (this.#desktopStateTimeout) {
      clearTimeout(this.#desktopStateTimeout);
      this.#desktopStateTimeout = null;
    }
  }

  /** Prefetches product HTML when the user shows intent to open quick add. */
  #scheduleModalPrefetch() {
    const chooseButton = this.querySelector('.quick-add__button--choose');
    if (!(chooseButton instanceof HTMLElement)) return;

    chooseButton.addEventListener(
      'pointerenter',
      () => {
        const url = this.productPageUrl;
        if (!url) return;

        const alreadyPrefetched = [...document.querySelectorAll('link[rel="prefetch"]')].some(
          (prefetchLink) => prefetchLink instanceof HTMLLinkElement && prefetchLink.href === url
        );
        if (alreadyPrefetched) return;

        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        link.setAttribute('fetchpriority', 'low');
        document.head.appendChild(link);
      },
      { once: true, passive: true }
    );
  }

  /** Wires hover/focus hydration for the desktop inline variant picker. */
  #scheduleDesktopPickerHydration() {
    if (isMobileBreakpoint()) return;

    const host = this.querySelector('[data-desktop-picker-host]');
    if (!host) return;

    const productCard = this.#getProductCardElement();
    const hydrate = () => this.#hydrateDesktopVariantPicker();

    productCard?.addEventListener('pointerenter', hydrate, { once: true, passive: true });
    productCard?.addEventListener('focusin', hydrate, { once: true });
    this.addEventListener('pointerenter', hydrate, { once: true, passive: true });
  }

  /** Clones the deferred desktop picker template into the live DOM. */
  #hydrateDesktopVariantPicker() {
    if (this.#desktopPickerHydrated || isMobileBreakpoint()) return;

    const host = this.querySelector('[data-desktop-picker-host]');
    if (!(host instanceof HTMLElement)) return;

    const template = host.querySelector('template.quick-add__desktop-variant-picker-template');
    if (!(template instanceof HTMLTemplateElement)) {
      this.#desktopPickerHydrated = true;
      return;
    }

    const feedback = host.querySelector('.quick-add__desktop-feedback');
    host.insertBefore(template.content.cloneNode(true), feedback);
    template.remove();
    host.dataset.hydrated = 'true';
    this.#desktopPickerHydrated = true;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    try {
      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#openQuickAddModal();
        return;
      }

      // If we could not load quick add content, fall back to product page.
      this.#navigateToProductPage();
    } catch {
      this.#navigateToProductPage();
    }
  };

  #navigateToProductPage() {
    const url = this.productPageUrl;
    if (url) {
      window.location.href = url;
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        return null;
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        return null;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productMedia = productGrid.querySelector('.product-information__media');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = this.#buildMobileProductTitleLink(this.dataset.productTitle || '');

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      productMedia?.remove();

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      const viewProductDetailsWrapper = document.createElement('div');
      viewProductDetailsWrapper.classList.add('quick-add-mobile-details-link__wrapper');
      const viewProductDetailsLink = document.createElement('a');
      viewProductDetailsLink.classList.add('quick-add-mobile-details-link');
      viewProductDetailsLink.textContent = 'View product details';
      viewProductDetailsLink.href = this.productPageUrl;

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      viewProductDetailsWrapper.appendChild(viewProductDetailsLink);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productGrid.appendChild(viewProductDetailsWrapper);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#refreshKlaviyoBackInStock(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Re-initializes Klaviyo BIS bindings for dynamically rendered modal content.
   * @param {Element} modalContent
   */
  #refreshKlaviyoBackInStock(modalContent) {
    const notifyTrigger = modalContent.querySelector('.notify_me');
    if (!notifyTrigger) return;

    const klaviyo = /** @type {{ load?: (feature: string) => void } | undefined} */ (
      /** @type {Window & { klaviyo?: unknown }} */ (window).klaviyo
    );

    if (klaviyo && typeof klaviyo.load === 'function') {
      try {
        klaviyo.load('backinstock');
      } catch {
        // BIS will continue to rely on default DOM scanning if load throws.
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener('click', this.#handleNotifyMeClick, { signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const mobileDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.quick-add-mobile-details-link')
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (mobileDetailsLink) mobileDetailsLink.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  /**
   * Closes quick add when Klaviyo notify trigger is clicked.
   * @param {MouseEvent} event
   */
  #handleNotifyMeClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const notifyTrigger = event.target.closest('.notify_me');
    if (!notifyTrigger || !this.contains(notifyTrigger)) return;

    this.closeDialog();
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
